/**
 * Channel settings (from the channel header): rename, topic and description, convert public ↔ private, archive /
 * unarchive, leave, mute and notification level. Edits are drafts until saved; every write is optimistic (rolled back
 * when the server refuses, with a notice) and queued offline. Permissions follow `logic/channels` (the server enforces).
 */
import type { ChannelKind, NotifyLevel, UpdateChannelRequest } from "@bundle/contracts";
import { msg, type MessageDescriptor } from "../i18n.ts";
import {
  channelNameError,
  channelNameHint,
  channelPermissions,
  knownChannelNames,
  normalizeChannelName,
  type ChannelPermissions,
} from "../logic/channels.ts";
import { canCloseConversation, canConvertToChannel } from "../logic/directMessages.ts";
import { muteNotice, notifyLevelNotice } from "../logic/notifyKinds.ts";
import { channelTitle } from "../logic/sidebar.ts";
import { memberName } from "../logic/workspace.ts";
import { ViewModel } from "./ViewModel.ts";
import type { WorkspaceViewModel } from "./WorkspaceViewModel.ts";

export type ChannelSettingsState = {
  channelId: string;
  /** The channel is in the cache. */
  found: boolean;
  title: string;
  kind?: ChannelKind;
  isDm: boolean;
  archived: boolean;
  memberCount: number;
  /** About: who created the channel and when (Slack's "Created by Ada on March 3, 2026"); absent for DMs. */
  createdBy?: string;
  createdAt?: number;
  /** Drafts. */
  name: string;
  topic: string;
  purpose: string;
  nameError?: MessageDescriptor;
  nameHint?: MessageDescriptor;
  /** A draft differs from the channel. */
  dirty: boolean;
  canSave: boolean;
  perms: ChannelPermissions;
  muted: boolean;
  notify: NotifyLevel;
  /** DMs and group DMs: "Close conversation". */
  canClose: boolean;
  /** Group DMs: "Convert to private channel" (I may create channels). */
  canConvertToChannel: boolean;
  /** The new channel's name (draft) and what's wrong with it. */
  convertName: string;
  convertError?: MessageDescriptor;
  /** A write the server refused, or "saved". */
  notice?: MessageDescriptor;
};

const NO_PERMS: ChannelPermissions = { isMember: false, canRename: false, canConvert: false, canArchive: false, canEditTopic: false, canLeave: false, canJoin: false };

export class ChannelSettingsViewModel extends ViewModel<ChannelSettingsState> {
  private readonly ws: WorkspaceViewModel;
  private readonly channelId: string;

  constructor(ws: WorkspaceViewModel, channelId: string) {
    super({
      channelId,
      found: false,
      title: "",
      isDm: false,
      archived: false,
      memberCount: 0,
      name: "",
      topic: "",
      purpose: "",
      dirty: false,
      canSave: false,
      perms: NO_PERMS,
      muted: false,
      notify: "default",
      canClose: false,
      canConvertToChannel: false,
      convertName: "",
    });
    this.ws = ws;
    this.channelId = channelId;
    this.onDispose(ws.subscribe(() => this.sync(false)));
    this.sync(true);
  }

  private get wsId(): string {
    return this.ws.state.wsId;
  }

  /** Follows the cache; drafts are replaced only when not being edited (or on `reset`). */
  private sync(resetDrafts: boolean): void {
    const cache = this.ws.state.cache;
    const c = cache?.channels[this.channelId];
    if (cache === undefined || c === undefined) {
      this.update({ found: false });
      return;
    }
    const ms = cache.memberships[this.channelId];
    const s = this.state;
    const keep = !resetDrafts && s.found && s.dirty;
    const next: ChannelSettingsState = {
      ...s,
      found: true,
      title: channelTitle(cache, this.channelId),
      kind: c.kind,
      isDm: c.kind === "dm" || c.kind === "group_dm",
      archived: c.archived,
      memberCount: c.memberCount,
      createdBy: c.kind === "dm" || c.kind === "group_dm" ? undefined : memberName(cache, c.createdBy),
      createdAt: c.kind === "dm" || c.kind === "group_dm" ? undefined : c.createdAt,
      name: keep ? s.name : c.name,
      topic: keep ? s.topic : c.topic ?? "",
      purpose: keep ? s.purpose : c.purpose ?? "",
      perms: channelPermissions(cache, this.channelId, this.ws.deps.clock.now()),
      muted: ms !== undefined && ms.muted,
      notify: ms === undefined ? "default" : ms.notify,
      canClose: canCloseConversation(cache, this.channelId),
      canConvertToChannel: canConvertToChannel(cache, this.channelId, this.ws.deps.clock.now()),
    };
    this.setState(this.validate(next));
  }

  private validate(s: ChannelSettingsState): ChannelSettingsState {
    const cache = this.ws.state.cache;
    const c = cache?.channels[this.channelId];
    if (cache === undefined || c === undefined) return s;
    const next = { ...s };
    const renaming = !s.isDm && normalizeChannelName(s.name) !== c.name;
    const error = renaming ? channelNameError(s.name, knownChannelNames(cache, this.channelId)) : undefined;
    if (error === undefined) delete next.nameError;
    else next.nameError = error;
    const hint = renaming ? channelNameHint(s.name) : undefined;
    if (hint === undefined) delete next.nameHint;
    else next.nameHint = hint;
    next.dirty = renaming || s.topic.trim() !== (c.topic ?? "") || s.purpose.trim() !== (c.purpose ?? "");
    next.canSave = next.dirty && error === undefined && (!renaming || s.perms.canRename) && (renaming || s.perms.canEditTopic);
    return next;
  }

  /** Drafts back to the channel's current values (each time the sheet opens). */
  reset(): void {
    this.update({ notice: undefined });
    this.sync(true);
  }

  setName(name: string): void {
    this.setState(this.validate({ ...this.state, name }));
  }

  setTopic(topic: string): void {
    this.setState(this.validate({ ...this.state, topic }));
  }

  setPurpose(purpose: string): void {
    this.setState(this.validate({ ...this.state, purpose }));
  }

  private write(req: UpdateChannelRequest, done?: MessageDescriptor): void {
    this.ws.deps.data.updateChannel(this.wsId, this.channelId, req).then(
      () => {
        if (done !== undefined && !this.isDisposed) this.update({ notice: done });
      },
      (e: unknown) => {
        if (this.isDisposed) return;
        const code = (e as { code?: string } | undefined)?.code;
        this.update({ notice: code === "name_taken" ? msg("channels.name.taken", { name: normalizeChannelName(req.name ?? "") }) : msg("channels.settings.failed") });
      },
    );
  }

  /** Saves the changed drafts (name, topic, description). */
  save(): void {
    const s = this.state;
    const c = this.ws.state.cache?.channels[this.channelId];
    if (!s.canSave || c === undefined) return;
    const req: UpdateChannelRequest = {};
    if (!s.isDm && normalizeChannelName(s.name) !== c.name) req.name = normalizeChannelName(s.name);
    if (s.topic.trim() !== (c.topic ?? "")) req.topic = s.topic.trim();
    if (s.purpose.trim() !== (c.purpose ?? "")) req.purpose = s.purpose.trim();
    this.write(req, msg("channels.settings.saved"));
    this.sync(true);
  }

  /** Public ↔ private. */
  setPrivate(on: boolean): void {
    if (!this.state.perms.canConvert) return;
    this.write({ kind: on ? "private" : "public" });
  }

  setArchived(on: boolean): void {
    if (!this.state.perms.canArchive) return;
    this.write({ archived: on });
  }

  leave(): void {
    if (!this.state.perms.canLeave) return;
    this.ws.deps.data.leaveChannel(this.wsId, this.channelId).catch(() => {
      if (!this.isDisposed) this.update({ notice: msg("channels.leave.failed") });
    });
  }

  setMuted(on: boolean): void {
    if (!this.state.perms.isMember) return;
    this.ws.deps.data.updateMembership(this.wsId, this.channelId, { muted: on });
    this.update({ notice: muteNotice(on) });
  }

  setNotify(level: NotifyLevel): void {
    if (!this.state.perms.isMember) return;
    this.ws.deps.data.updateMembership(this.wsId, this.channelId, { notify: level });
    this.update({ notice: notifyLevelNotice(level) });
  }

  /** Join from the channel screen (public channel I'm viewing). */
  async join(): Promise<void> {
    try {
      await this.ws.deps.data.joinChannel(this.wsId, this.channelId);
    } catch {
      if (!this.isDisposed) this.update({ notice: this.ws.state.network ? msg("channels.join.failed") : msg("channels.join.offline") });
    }
  }

  /** "Close conversation" (DMs): out of my sidebar until someone posts in it or I open it again. */
  closeConversation(): void {
    if (!this.state.canClose) return;
    this.ws.deps.data.updateMembership(this.wsId, this.channelId, { closed: true });
  }

  setConvertName(name: string): void {
    this.update({ convertName: name, convertError: undefined });
  }

  /**
   * "Convert to private channel" (group DMs): same people and history, under the name typed. Resolves true once sent
   * (optimistic), false when the name isn't valid (`convertError` says why).
   */
  convertToChannel(): boolean {
    const cache = this.ws.state.cache;
    if (!this.state.canConvertToChannel || cache === undefined) return false;
    const error = channelNameError(this.state.convertName, knownChannelNames(cache, this.channelId));
    if (error !== undefined) {
      this.update({ convertError: error });
      return false;
    }
    this.write({ kind: "private", name: normalizeChannelName(this.state.convertName) });
    this.update({ convertName: "", convertError: undefined });
    return true;
  }

  dismissNotice(): void {
    this.update({ notice: undefined });
  }
}
